import { Collection } from 'firestorter';
import format from 'string-template';

import { firestorePaths, pathPlaceholder } from '../utils/paths';
import BaseDocument from './BaseDocument';

const PRICING_PACKAGES = {
  THREE_MONTHS: '3',
  SIX_MONTHS: '6',
  TWELVE_MONTHS: '12',
  STUDENT_BELOW_18: 'Student/Below 18',
};

const FieldLabels = {
  CoachName: 'coachName',
  CoachNamePronunciation: 'coachNamePronunciation',
  CoachLocation: 'coachLocation',
  Pricing: 'pricing',
  SpecificRequirements: 'specificRequirements',
  TrainingProgramme: 'trainingProgramme',
  NutritionPlan: 'nutritionPlan',
  PointOfContact: 'pointOfContact',
  ResponseTime: 'responseTime',
  ValuesAndBeliefs: 'valuesAndBeliefs',
  EducationAndQualification: 'educationAndQualification',
  ClientRequirementsAccepts: 'clientRequirementsAccepts',
  ClientRequirementsDoesNotAccept: 'clientRequirementsDoesNotAccept',
  Personas: 'personas',
  ContactEmail: 'contactEmail',
  WebsiteURL: 'websiteURL',
  Instagram: 'instagram',
  Tiktok: 'tiktok',
};

/**
 * Class representing coach information used by IS users.
 * This model stores coach information impotent for IS users when engaging with a lead.
 *
 * @class CoachInfo
 * @extends BaseDocument
 */
class CoachInfo extends BaseDocument {
/**
 * Retrieves the coach information note.
 * If no note is available, it returns an empty string.
 *
 * @returns {string} The stored coach information note.
 */
  get coachInfoNote() {
    return this.data.coachInfoNote || '';
  }

  /**
   * Retrieves whether the coach information is current or not.
   *
   * @returns {boolean} Whether the coach information is current or not.
   */
  get current() {
    return !!this.data.current;
  }

  /**
   * Gets the coach's name.
   * @returns {string}
   */
  get coachName() {
    return this.data.coachName || '';
  }

  /**
   * Gets the coach's name pronunciation.
   * @returns {string}
   */
  get coachNamePronunciation() {
    return this.data.coachNamePronunciation || '';
  }

  /**
   * Gets the coach's location.
   * @returns {string}
   */
  get coachLocation() {
    return this.data.coachLocation || '';
  }

  /**
   * Gets the specific requirements to mention on calls.
   * @returns {string}
   */
  get specificRequirements() {
    return this.data.specificRequirements || '';
  }

  /**
   * Gets the training programme.
   * @returns {string}
   */
  get trainingProgramme() {
    return this.data.trainingProgramme || '';
  }

  /**
   * Gets the nutrition plan.
   * @returns {string}
   */
  get nutritionPlan() {
    return this.data.nutritionPlan || '';
  }

  /**
   * Gets the point of contact (coach/client).
   * @returns {string}
   */
  get pointOfContact() {
    return this.data.pointOfContact || '';
  }

  /**
   * Gets the response time in chat.
   * @returns {string}
   */
  get responseTime() {
    return this.data.responseTime || '';
  }

  /**
   * Gets the coach's values and beliefs.
   * @returns {string}
   */
  get valuesAndBeliefs() {
    return this.data.valuesAndBeliefs || '';
  }

  /**
   * Gets the coach's education and qualification.
   * @returns {string}
   */
  get educationAndQualification() {
    return this.data.educationAndQualification || '';
  }

  /**
   * Gets the client requirements the coach accepts.
   * @returns {string}
   */
  get clientRequirementsAccepts() {
    return this.data.clientRequirementsAccepts || '';
  }

  /**
   * Gets the client requirements the coach does not accept.
   * @returns {string}
   */
  get clientRequirementsDoesNotAccept() {
    return this.data.clientRequirementsDoesNotAccept || '';
  }

  /**
   * Gets the personas.
   * @returns {string}
   */
  get personas() {
    return this.data.personas || '';
  }

  /**
   * Gets the contact email for inbound to coach.
   * @returns {string}
   */
  get contactEmail() {
    return this.data.contactEmail || '';
  }

  /**
   * Gets the website URL.
   * @returns {string}
   */
  get websiteURL() {
    return this.data.websiteURL || '';
  }

  /**
   * Gets the Instagram handle.
   * @returns {string}
   */
  get instagram() {
    return this.data.instagram || '';
  }

  /**
   * Gets the TikTok handle.
   * @returns {string}
   */
  get tiktok() {
    return this.data.tiktok || '';
  }

  /**
   * Gets the pricing array.
   * @returns {Array}
   */
  get pricing() {
    return this.data.pricing || [];
  }

  /**
 * Updates the coach information with the provided value.
 *
 * @param {string} data - The new coach information to be stored.
 * @returns {Promise<void>} A promise that resolves when the update operation is complete.
 */
  async updateCoachInfos(data) {
    await this.ref.set(data);
  }

  /**
   * Creates a new CoachInfo document under a specific coachId.
   *
   * @param {string} coachId - The ID of the coach.
   * @param {string} data - The new coach information to be stored.
   * @returns {Promise<string>} The created document.
   */
  static async createCoachInfo(coachId, data) {
    const coachInfoCollection = new Collection(
      format(firestorePaths.COACH_INFO_DOC, { [pathPlaceholder.COACH_ID]: coachId }),
      { createDocument: (source, options) => new CoachInfo(source, options) },
    );
    await coachInfoCollection.add({
      ...data,
    });
    await CoachInfo.initCollection(coachInfoCollection);

    return coachInfoCollection.docs[0];
  }

  /**
   * Retrieves a specific CoachInfo documents by coach ID.
   *
   * @param {string} coachId - The ID of the coach.
   * @returns {Collection} The CoachInfo collection instance.
   */
  static async getCoachInfosByCoachId(coachId) {
    const coachInfoCollection = new Collection(
      format(firestorePaths.COACH_INFO_DOC, { [pathPlaceholder.COACH_ID]: coachId }),
      { createDocument: (source, options) => new CoachInfo(source, options) },
    );
    await CoachInfo.initCollection(coachInfoCollection);

    return coachInfoCollection;
  }
}

export {
  CoachInfo,
  PRICING_PACKAGES,
  FieldLabels,
};
